import { useRef } from 'react';
import { Accordion, AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger, Button } from '@c2fo/liquidity';
import { PhoneIcon, InboxIcon, CalendarLinesIcon } from '@c2fo/liquidity/icons';
import bank from '@/enterprise/assets/bank.svg?url';
import box from '@/enterprise/assets/box.svg?url';
import businesshandshake from '@/enterprise/assets/businesshandshake.svg?url';
import discussion from '@/enterprise/assets/discussion.svg?url';
import documentcheck from '@/enterprise/assets/documentcheck.svg?url';
import factory from '@/enterprise/assets/factory.svg?url';
import invoices from '@/enterprise/assets/invoices.svg?url';
import NetworkIcon, {
  LENDING_NETWORK_PRIMARY_FILL,
  LENDING_NETWORK_SECONDARY_FILL,
} from '@/enterprise/assets/NetworkIcon';
import piechart from '@/enterprise/assets/piechart.svg?url';
import reviewingcharts from '@/enterprise/assets/reviewingcharts.svg?url';
import schedulingoffers from '@/enterprise/assets/schedulingoffers.svg?url';
import { useReporting } from '@/reporting';
import { ButtonLink } from '@/shared/components/ButtonLink';
import Divider from '@/shared/components/Divider';

const benefits = [
  {
    icon: factory,
    title: 'Asset Based Lending (ABL)',
    description:
      'Provides immediate access to capital using assets like receivables, inventory or equipment as collateral.',
  },
  {
    icon: piechart,
    title: 'Term Loans',
    description:
      'Provides additional capital for short and intermediate time horizons, with flexible pricing and paydown provisions.',
  },
  {
    icon: invoices,
    title: 'Receivables Finance',
    description:
      'Supplies growing companies with an on-demand line of credit. Funding amount is limited only by your outstanding invoices.',
  },
  {
    icon: documentcheck,
    title: 'Purchase Order Financing (PO Financing)',
    description:
      'Covers the upfront costs of purchasing inventory or manufacturing inputs, allowing your company to fulfill orders and grow.',
  },
  {
    icon: bank,
    title: 'Invoice Financing',
    description: `Convert your invoices that are not on C2FO’s platform into working capital with ease and transparency. Unlike many other providers, our partners don’t require you to sell all invoices.`,
  },
  {
    icon: box,
    title: 'Production Financing',
    description:
      'Access financing based on your company’s payables for the entire production lifecycle. Pay your vendors and the costs of raw materials while maintaining liquidity for other business operations.',
  },
];

type HowItWorksStep =
  | { title: string; subtitle: string; imageSrc: string }
  | { title: string; subtitle: string; useNetworkIcon: true };

const howItWorks: HowItWorksStep[] = [
  {
    title: 'Innovative Funding Strategies',
    subtitle:
      'Our advisors will work with you to find the best solutions — helping you determine the products and lenders best positioned to support your growing business.',
    imageSrc: discussion,
  },
  {
    title: 'Responsive and Convenient',
    subtitle:
      'Your credit availability moves in real time, based on your open invoices, purchase orders and other available collateral. Our intuitive technology and team of experienced working capital advisors stand ready to help you initiate the funding process with ease.',
    imageSrc: reviewingcharts,
  },
  {
    title: 'Trustworthy',
    subtitle: `Finding the right lender can be a challenge in today's marketplace, but we've got you covered. Our network of trusted funding partners provide timely and transparent terms, along with excellent customer service.`,
    useNetworkIcon: true,
  },
  {
    title: 'Flexible and Fast',
    subtitle:
      'Our flexible process works with you to meet your needs. Our lending partners offer financing at lower deal minimums and adaptive lending processes to help ensure you qualify for the financing your company needs.',
    imageSrc: schedulingoffers,
  },
];

const faqs = [
  {
    question: 'What is your business model?',
    answer:
      'We partner with a carefully vetted network of alternative and non-bank lenders that focus on providing fair and accessible funding solutions to businesses. We help match you with the right lender from our network, based on your unique needs.',
  },
  {
    question: 'What types of financing referrals do you offer?',
    answer:
      'We work with partners that offer a wide range of financing options, including: term loans, asset-based lines of credit, purchase order (PO) financing, invoice financing, and business lines of credit.',
  },
  {
    question: 'What is the range of financing available through your referral partners?',
    answer: `Our network offers flexible solutions, ranging from small lines of credit to financing up to $100 million, depending on your business’s needs and qualifications.`,
  },
  {
    question: 'How do I apply for financing?',
    answer:
      'There’s no formal application process with us. Instead, we conduct a discovery call to understand your business and financial needs. Based on that conversation, we’ll connect you with the right lender.',
  },
  {
    question: 'How do you vet your lending partners?',
    answer:
      'We ensure all our partners are reliable, transparent and prioritize offering fair market pricing and adequate access to funding. We only work with lenders with a proven track record of integrity and quality service.',
  },
  {
    question: 'Will I need to provide financial documentation?',
    answer:
      'Yes, depending on the type of financing you’re seeking, your lender may request financial documents such as balance sheets, income statements or tax returns. However, our discovery call helps us streamline the process to refer you to lenders that best fit your needs and profile.',
  },
  {
    question: 'How long does it take to get funding?',
    answer: `Once we’ve matched you with the right partner, the timeline for funding varies depending on the product. In general, your lender could provide a proposal within 2-5 business days, with funding often available within a few weeks, depending on the complexity of the financing.`,
  },
  {
    question: 'Is there any cost to use your service?',
    answer: `No, our service is free to businesses. We are compensated by our lending partners when we successfully match you with a financing solution. There are no fees for the initial discovery call or for being introduced to our network of lenders.`,
  },
  {
    question: 'How do I get started?',
    answer: `Simply schedule a discovery call with us. We'll take the time to understand your business needs, then connect you with the best lending partner for your situation.`,
  },
];

const Lending = () => {
  const { trackEnterpriseEvent } = useReporting();

  const contactRef = useRef<HTMLDivElement>(null);
  const handleConnectClick = () => {
    trackEnterpriseEvent('lending::schedule-call::clicked', { referrer: 'lending-marketing-page' });
    const { current } = contactRef;
    if (current) {
      current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="mx-auto max-w-full space-y-16 md:max-w-7xl">
      {/* Hero Banner */}
      <div className="grid grid-cols-1 items-center gap-4 rounded-2xl bg-primary-500 px-8 py-6 shadow-md md:grid-cols-3 md:px-16 md:py-12">
        <div className="col-span-1 row-start-2 space-y-4 text-white md:col-span-2 md:row-start-1">
          <p className="text-4xl">Lending Connections</p>
          <p className="text-2xl">Need more funding to boost your working capital?</p>
          <p className="pb-2 text-base">
            C2FO offers business lending referrals through our network of trusted partners to give your company access
            to any additional financing you need to ensure your business thrives.*
          </p>
          <Button variant="primary" onClick={handleConnectClick}>
            Schedule a Call
          </Button>
          <p className="pt-2 text-xs italic">
            *C2FO is solely acting as a reference for third party lenders; all lending decisions and servicing are done
            by an unaffiliated third party. By connecting with us, you acknowledge that C2FO may receive a referral fee
            from a lender and you give C2FO permission to provide your information to lenders for the purpose of such
            referral.
          </p>
        </div>
        <div className="col-span-1 flex justify-center">
          <img src={businesshandshake} alt="businesshandshake" className="h-52 w-52" />
        </div>
      </div>
      <Divider />
      {/* Benefits */}
      <div className="flex flex-col items-center space-y-4">
        <p className="text-3xl">Flexible Financing Options</p>
        <p className="text-lg">
          We refer you to lenders that offer a variety of funding solutions to fit your business needs and keep you in
          the driver’s seat.
        </p>
        <div className="grid grid-cols-1 gap-4 rounded-2xl bg-white p-8 shadow-md md:grid-cols-2">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="grid grid-cols-1 gap-2 py-3 md:grid-cols-5">
              <div className="col-span-1 flex justify-center">
                <img src={benefit.icon} alt={benefit.title} className="h-20 w-20" />
              </div>
              <div className="col-span-4">
                <p className="text-lg font-medium">{benefit.title}</p>
                <p className="text-base">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Divider />
      {/* How It Works */}
      <div className="flex flex-col items-center space-y-4">
        <p className="text-3xl">Building a Better Lending Solution</p>
        <p className="max-w-5xl text-center text-lg">
          As your company continues to evolve, so do your working capital needs. Our advisors work directly with you to
          provide multiple referral options to find the right lender.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2">
          {howItWorks.map((step) => (
            <div key={step.title} className="flex max-w-xl flex-col items-center p-6">
              {'useNetworkIcon' in step ? (
                <NetworkIcon
                  primaryFill={LENDING_NETWORK_PRIMARY_FILL}
                  secondaryFill={LENDING_NETWORK_SECONDARY_FILL}
                  className="h-48 w-48"
                  aria-hidden
                />
              ) : (
                <img src={step.imageSrc} alt={step.title} className="h-48 w-48" />
              )}
              <p className="text-lg font-medium">{step.title}</p>
              <p className="pt-1 text-base">{step.subtitle}</p>
            </div>
          ))}
        </div>
      </div>
      <Divider />
      {/* Contact */}
      <div className="m-auto flex max-w-5xl flex-col items-center space-y-4" ref={contactRef}>
        <p className="text-3xl">Get Connected</p>
        <p className="text-center text-lg">
          Learn more about C2FO’s referral partners and how you can secure funding with a lender for your growing
          business.
        </p>
        <p className="text-lg">Book an appointment using our online scheduler.</p>
        <ButtonLink
          icon={<CalendarLinesIcon className="h-5 w-5" />}
          variant="secondary"
          to="https://calendly.com/c2fo-jay-lott/30-minute-meeting?hide_event_type_details=1"
          target="_blank"
          onClick={() =>
            trackEnterpriseEvent('lending::contact::appointment::clicked', { referrer: 'lending-marketing-page' })
          }
        >
          Schedule Appointment
        </ButtonLink>
        <p className="text-lg">Contact Us</p>
        <div className="flex flex-col gap-2 md:flex-row">
          <Button
            icon={<PhoneIcon className="h-5 w-5" />}
            variant="secondary"
            onClick={() => {
              trackEnterpriseEvent('lending::contact::phone-number::clicked', { referrer: 'lending-marketing-page' });
              window.open(`tel:9139514798`);
            }}
          >
            913-951-4798
          </Button>
          <Button
            icon={<InboxIcon className="h-5 w-5" />}
            variant="secondary"
            onClick={() => {
              trackEnterpriseEvent('lending::contact::email::clicked', { referrer: 'lending-marketing-page' });
              window.open(`mailto:lending.advisors@c2fo.com`);
            }}
            className="lowercase"
          >
            lending.advisors@c2fo.com
          </Button>
        </div>
      </div>
      <Divider />
      {/* FAQs */}
      <div className="flex flex-col items-center space-y-4" id="lending-faqs">
        <p className="text-3xl">Frequently Asked Questions</p>
        <p className="text-center text-lg">
          At C2FO, we’re committed to helping businesses access flexible, fast and affordable funding solutions. Our FAQ
          below answers our most common questions. Please schedule a call or contact us for personalized assistance.
        </p>
        <Accordion type="multiple" className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {faqs.map((faq) => (
            <AccordionItem
              key={faq.question}
              value={faq.question}
              className="max-h-fit max-w-lg rounded-2xl bg-white p-4 shadow-md"
            >
              <AccordionHeader className="flex justify-between space-x-6">
                <p className="text-lg font-medium">{faq.question}</p>
                <AccordionTrigger />
              </AccordionHeader>
              <AccordionContent className="px-6">
                <p className="text-text-secondary">{faq.answer}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

export default Lending;
